import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	CircularProgress,
	Drawer,
	Typography,
} from "@mui/material";
import { FormattedDate } from "utils/DateUtils";
import { intl } from "LocaleWrapper";
import { FormGroup } from "@cofano/cck";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactNode, useEffect, useState } from "react";
import {
	ApplicationDefinedStatus,
	ApplicationDefinedStatusChange,
} from "view/statuses/components/changer/ApplicationDefinedStatusChanger";

type ApplicationDefinedStatusHistoryProps<T extends ApplicationDefinedStatus, U extends ApplicationDefinedStatusChange<T>,> = {
	open: boolean;
	close: () => void;
	initStatusChanges: () => Promise<U[]>;
	historyTitle: ReactNode;
	render: (statusChange: U) => ReactNode;
};

export const ApplicationDefinedStatusHistory = <T extends ApplicationDefinedStatus, U extends ApplicationDefinedStatusChange<T>,>({
	open,
	close,
	initStatusChanges,
	historyTitle,
	render,
}: ApplicationDefinedStatusHistoryProps<T, U>) => {
	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={() => close()}
		>
			<Box
				padding={2}
				sx={{
					width: "400px",
					display: "flex",
					flexDirection: "column",
					flexGrow: 1,
				}}
			>
				<ApplicationDefinedStatusHistoryDrawer
					initStatusChanges={initStatusChanges}
					historyTitle={historyTitle}
					render={render}
				/>
			</Box>
		</Drawer>
	);
};

const ApplicationDefinedStatusHistoryDrawer = <T extends ApplicationDefinedStatus, U extends ApplicationDefinedStatusChange<T>,>({
	initStatusChanges,
	historyTitle,
	render,
}: Omit<ApplicationDefinedStatusHistoryProps<T, U>, "open" | "close">) => {

	const [ statusChanges, setStatusChanges ] = useState<U[] | null>(null);

	const [ openStatusChangeId, setOpenStatusChangeId ] = useState<number | null>(null);

	useEffect(() => {
		let ignore = false;

		initStatusChanges().then(statusChanges => {
			if (!ignore) {
				const sortedStatusChanges = statusChanges.toSorted((a, b) => (b.createdOn ?? 0) - (a.createdOn ?? 0));

				setStatusChanges(sortedStatusChanges);
				setOpenStatusChangeId(sortedStatusChanges[0]?.id ?? null);
			}
		});

		return () => {
			ignore = true;
		};
	}, []);

	return (
		<>
			<h2>{historyTitle}</h2>

			{statusChanges !== null ? statusChanges.map((statusChange) => (
				<Accordion
					key={statusChange.id}
					expanded={openStatusChangeId === statusChange.id}
					style={{ borderLeft: `3px solid ${statusChange.status.color}` }}
					onChange={() => setOpenStatusChangeId(openStatusChangeId === statusChange.id ? null : statusChange.id)}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography style={{ alignItems: "center", display: "flex", justifyContent: "space-between", width: "100%" }}>
							{intl.formatMessage({ id: `${statusChange.status.languageKeyPrefix}${statusChange.status.name}` })}
						</Typography>
					</AccordionSummary>

					<AccordionDetails>
						<Typography>
							{render(statusChange)}

							<div style={{ opacity: 0.6 }}>
								<FormGroup label={intl.formatMessage({ id: "APPLIED_BY" })} overflow="hidden">
									<span>
										<small>
											{statusChange.appliedBy.displayName || statusChange.appliedBy.username}
											{" - "}
											<FormattedDate date={statusChange.createdOn} withoutYear />
										</small>
									</span>
								</FormGroup>
							</div>
						</Typography>
					</AccordionDetails>
				</Accordion>
			)) : (
				<Box sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexGrow: 1,
				}}>
					<CircularProgress size={24} />
				</Box>
			)}
		</>
	);
};

export default ApplicationDefinedStatusHistory;
